// Consented recording of a device's web UI.
//
// The shape of this hook follows the consent model rather than the other
// way round. Two things drive it:
//
//  1. Recording is never implicit. A session records only after the
//     customer has explicitly agreed, and agreeing is what creates the
//     consent record the gateway demands before it will record anything.
//
//  2. Recording starts by *reopening* the session, not by flipping a
//     switch on the live one. The login exchange is the most useful thing
//     in a capture — a vendor profile is authored from the login form —
//     and a recording that begins after the user has logged in has missed
//     it. Reopening means capture starts at the first request, and the
//     re-login the user performs is both what gets captured and a visible
//     signal that recording began.
//
// Nothing leaves the customer's network until they review the summary and
// approve it; the artifact sits on their gateway until then, and expires
// if they never decide.

import { useCallback, useState } from 'react'
import axios from 'axios'
import { config } from '../config'

/** Per-category counts of what redaction removed. */
export interface RedactionCounts {
  headers: number
  cookies: number
  queryParams: number
  bodyFields: number
  literalMatches: number
  total: number
}

/** What the customer reviews before deciding. */
export interface CaptureSummary {
  captureId: string
  exchangeCount: number
  pageUrls: string[]
  expiresAt: string
  sizeBytes: number
  redaction: RedactionCounts
}

export type CaptureStage =
  /** Browsing normally; nothing recorded. */
  | 'off'
  /** Consent granted, session reopening with recording armed. */
  | 'starting'
  /** Recording. */
  | 'recording'
  /** Stopped; summary awaiting the customer's decision. */
  | 'review'
  /** Decision sent. */
  | 'done'

export interface UseWebCaptureReturn {
  stage: CaptureStage
  consentId: string | null
  summary: CaptureSummary | null
  error: string | null
  /**
   * Record consent. Returns the id to reopen the session with.
   *
   * `note` is what the customer says they were trying to do, on a recording
   * they started themselves. Optional — a recording is never blocked on
   * writing prose, and a prompted one already answers a stated question.
   */
  grantConsent: (disclosureText: string, note?: string, captureRequestId?: string) => Promise<string>
  /**
   * Arm recording on the session already open, without reopening it.
   *
   * For an ask about a device we already support, the login page is a page we
   * already understand — so making the customer log in again to reach the
   * thing we actually asked about is a poor way to accept a favour.
   */
  startInPlace: (sessionId: string, consentId: string) => Promise<void>
  /**
   * Stop recording and fetch the summary. Leaves browsing open.
   *
   * Resolves false when the stop failed: the recording is then still in the
   * session, and closing the session would throw it away.
   */
  stop: (sessionId: string) => Promise<boolean>
  /** Send the capture. The only path by which it leaves the network. */
  approve: () => Promise<void>
  /** Destroy it on the gateway. */
  discard: (reason?: string) => Promise<void>
  markRecording: () => void
  reset: () => void
}

export function useWebCapture(deviceId: string): UseWebCaptureReturn {
  const [stage, setStage] = useState<CaptureStage>('off')
  const [consentId, setConsentId] = useState<string | null>(null)
  const [summary, setSummary] = useState<CaptureSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const base = `${config.backendApi}/api/devices/${deviceId}`

  const grantConsent = useCallback(
    async (disclosureText: string, note = '', captureRequestId?: string): Promise<string> => {
      setError(null)
      setStage('starting')
      try {
        // The exact on-screen wording is sent with the consent so the
        // stored record reflects what the customer actually read, not what
        // some later version of this component happens to say.
        const res = await axios.post<{ consentId: string }>(
          `${base}/web-capture-consent`,
          // The ask rides on the consent, which is what ties a stored capture
          // back to the question it answered — and so what lets an ask stop
          // once enough people have answered it.
          { disclosureText, note, captureRequestId },
          { withCredentials: true },
        )
        setConsentId(res.data.consentId)
        return res.data.consentId
      } catch (e) {
        setStage('off')
        setError('Could not record your consent. Nothing has been captured.')
        throw e
      }
    },
    [base],
  )

  const startInPlace = useCallback(
    async (sessionId: string, consentId: string) => {
      setError(null)
      try {
        await axios.post(
          `${base}/web-proxy/capture/start`,
          { consentId },
          { withCredentials: true, headers: { 'X-Proxy-Session-Id': sessionId } },
        )
        setStage('recording')
      } catch (e) {
        setStage('off')
        setError('Could not start recording. Nothing has been captured.')
        throw e
      }
    },
    [base],
  )

  const markRecording = useCallback(() => { setStage('recording') }, [])

  const stop = useCallback(
    async (sessionId: string) => {
      setError(null)
      try {
        const res = await axios.post<{ capture: CaptureSummary | null }>(
          `${base}/web-proxy/capture/stop`,
          {},
          { withCredentials: true, headers: { 'X-Proxy-Session-Id': sessionId } },
        )
        if (res.data.capture) {
          setSummary(res.data.capture)
          setStage('review')
        } else {
          // Nothing was recorded — stopped immediately, or the session had
          // already ended. No artifact means nothing to review or decide.
          setStage('off')
        }
        return true
      } catch {
        setError('Could not stop the recording. Nothing has been sent.')
        return false
      }
    },
    [base],
  )

  const decide = useCallback(
    async (path: string, body: Record<string, string>) => {
      if (!summary) return
      setError(null)
      try {
        await axios.post(
          `${base}/web-proxy/capture/${path}`,
          { captureId: summary.captureId, ...body },
          { withCredentials: true },
        )
        setStage('done')
      } catch {
        // The capture is still on the gateway, so this is retryable rather
        // than lost — say so instead of implying it went somewhere.
        setError(
          `Could not ${path} the recording. It is still on your gateway and will expire on its own if you do nothing.`,
        )
      }
    },
    [base, summary],
  )

  const approve = useCallback(() => decide('approve', {}), [decide])
  const discard = useCallback(
    (reason?: string) => decide('discard', { reason: reason ?? '' }),
    [decide],
  )

  const reset = useCallback(() => {
    setStage('off')
    setConsentId(null)
    setSummary(null)
    setError(null)
  }, [])

  return {
    stage,
    consentId,
    summary,
    error,
    grantConsent,
    startInPlace,
    stop,
    approve,
    discard,
    markRecording,
    reset,
  }
}
